import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import * as quoteApi from '../../api/quotes';
import { Quote, SupplierRate } from '../../types/quote.types';
import SupplierSelectionModal from '../../components/Quote/SupplierSelectionModal';
import PricingConfirmationModal from '../../components/Quote/PricingConfirmationModal';
import ContractCreationModal from '../../components/Quote/ContractCreationModal';
import QuoteRejectionModal from '../../components/Quote/QuoteRejectionModal';
import * as contractApi from '../../api/contracts';
import QuoteList from '../../components/Quote/QuoteList';
import ContactSelectDialog from '../../components/common/ContactSelectDialog';
import { parseApiError } from '../../utils/errorHandler';

interface QuoteListPageProps {
  clientId?: string;
  propertyId?: string;
}

const QuoteListPage: React.FC<QuoteListPageProps> = ({ clientId, propertyId }) => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [supplierSelectionModal, setSupplierSelectionModal] = useState<{
    open: boolean;
    quote: Quote | null;
  }>({ open: false, quote: null });
  const [pricingConfirmationModal, setPricingConfirmationModal] = useState<{
    open: boolean;
    quote: Quote | null;
    selectedSupplier: SupplierRate | null;
  }>({ open: false, quote: null, selectedSupplier: null });
  const [contractCreationModal, setContractCreationModal] = useState<{
    open: boolean;
    quote: Quote | null;
    selectedSupplier: SupplierRate | null;
  }>({ open: false, quote: null, selectedSupplier: null });
  const [rejectionModal, setRejectionModal] = useState<{
    open: boolean;
    quote: Quote | null;
  }>({ open: false, quote: null });
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  // Helper to display errors with snackbar
  const displayError = useCallback((error: any, fallbackMessage: string) => {
    const parsed = parseApiError(error);
    enqueueSnackbar(parsed.message || fallbackMessage, { variant: 'error' });
    if (parsed.fieldErrors) {
      Object.entries(parsed.fieldErrors).forEach(([field, message]) => {
        const formattedField = field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
        enqueueSnackbar(`${formattedField}: ${message}`, { variant: 'error' });
      });
    }
  }, [enqueueSnackbar]);
  
  const fetchQuotes = useCallback(async () => {
    try {
      setLoading(true);
      let quotes;
      
      if (propertyId) {
        quotes = await quoteApi.getPropertyQuotes(propertyId);
      } else if (clientId) {
        quotes = await quoteApi.getClientQuotes(clientId);
      } else {
        quotes = await quoteApi.getAllQuotes();
      }
      
      if (Array.isArray(quotes)) {
        setQuotes(quotes);
      } else {
        setQuotes([]);
      }
    } catch (error) {
      displayError(error, 'Failed to fetch quotes');
    } finally {
      setLoading(false);
    }
  }, [clientId, propertyId, displayError]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const handleCreateQuote = () => {
    navigate('/utilities/quotes/new');
  };

  const handleViewQuote = (quoteId: string) => {
    navigate(`/utilities/quotes/${quoteId}/view`);
  };

  const handlePrintQuote = async (quoteId: string, clientName: string) => {
    try {
      await quoteApi.downloadQuotePdf(quoteId, clientName);
      enqueueSnackbar('Quote PDF downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error downloading quote PDF:', error);
      displayError(error, 'Failed to download quote PDF');
    }
  };

  // Audit 07 R-5: sending a quote deliberately picks the addressee Contact
  // (or explicitly keeps the PRIMARY default) before issue.
  const [sendTarget, setSendTarget] = useState<{ quoteId: string; clientId?: string } | null>(null);

  const handleSendQuote = (quoteId: string) => {
    const quote = quotes.find(q => q._id === quoteId);
    setSendTarget({ quoteId, clientId: quote?.client?._id });
  };

  const confirmSendQuote = async (contactId?: string) => {
    if (!sendTarget) return;
    try {
      await quoteApi.sendQuote(sendTarget.quoteId, contactId);
      enqueueSnackbar('Quote sent successfully', { variant: 'success' });
      fetchQuotes();
    } catch (error) {
      displayError(error, 'Failed to send quote');
    } finally {
      setSendTarget(null);
    }
  };

  const handleDelete = async (quoteId: string) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) {
      return;
    }

    try {
      await quoteApi.deleteQuote(quoteId);
      enqueueSnackbar('Quote deleted successfully', { variant: 'success' });
      fetchQuotes();
    } catch (error) {
      displayError(error, 'Failed to delete quote');
    }
  };

  const handleAcceptQuote = async (quoteId: string) => {
    // Accept quote should trigger the supplier selection modal workflow
    try {
      const quote = await quoteApi.getQuote(quoteId);
      if (quote.supplierRates.length === 0) {
        enqueueSnackbar('No supplier rates available for this quote', { variant: 'error' });
        return;
      }
      setSupplierSelectionModal({ open: true, quote });
    } catch (error) {
      displayError(error, 'Failed to load quote details');
    }
  };

  const handleRejectQuote = async (quoteId: string) => {
    try {
      const quote = await quoteApi.getQuote(quoteId);
      setRejectionModal({ open: true, quote });
    } catch (error) {
      displayError(error, 'Failed to load quote details');
    }
  };

  const handleQuoteRejection = async (quoteId: string, rejectionReason: string) => {
    try {
      await quoteApi.updateQuoteStatus(quoteId, { 
        status: 'REJECTED',
        rejectionReason 
      });
      enqueueSnackbar('Quote rejected successfully', { variant: 'success' });
      setRejectionModal({ open: false, quote: null });
      fetchQuotes();
    } catch (error: any) {
      displayError(error, 'Failed to reject quote');
      throw error;
    }
  };

  const handleSupplierSelected = (quote: Quote, selectedSupplier: SupplierRate) => {
    setSupplierSelectionModal({ open: false, quote: null });
    setPricingConfirmationModal({ open: true, quote, selectedSupplier });
  };

  const handlePricingConfirmed = async (quote: Quote, selectedSupplier: SupplierRate) => {
    try {
      // Audit 12: selection goes through the single lifecycle mechanism —
      // /select-supplier never existed server-side. The SupplierRate object is
      // sent so the server can match it against the quote's supplierRates.
      await quoteApi.updateQuoteStatus(quote._id, {
        status: 'SELECTED',
        selectedSupplier
      });
      
      enqueueSnackbar('Quote selected successfully', { variant: 'success' });
      setPricingConfirmationModal({ open: false, quote: null, selectedSupplier: null });
      fetchQuotes();
      
      // Show contract creation modal
      setContractCreationModal({ open: true, quote, selectedSupplier });
    } catch (error) {
      displayError(error, 'Failed to select quote');
    }
  };

  const handleContractCreation = async (contractData: any) => {
    const { quote, selectedSupplier } = contractCreationModal;
    if (!quote || !selectedSupplier) return;

    try {
      // Pass the status from contractData to the API
      const contract = await contractApi.createContractFromQuote(quote, selectedSupplier, contractData.status);
      enqueueSnackbar('Contract created successfully', { variant: 'success' });
      setContractCreationModal({ open: false, quote: null, selectedSupplier: null });
      
      // Navigate to the new contract
      navigate(`/utilities/contracts/${contract._id}`);
    } catch (error: any) {
      displayError(error, 'Failed to create contract');
      throw error;
    }
  };

  const getPageTitle = () => {
    if (propertyId) {
      return 'Property Quotes';
    } else if (clientId) {
      return 'Client Quotes';
    }
    return 'All Quotes';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold">
          {getPageTitle()}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateQuote}
        >
          Create Quote
        </Button>
      </Box>

      <QuoteList
        quotes={quotes}
        loading={loading}
        onViewQuote={handleViewQuote}
        onPrintQuote={(quoteId) => {
          // Find the quote to get the client name
          const quote = quotes.find(q => q._id === quoteId);
          if (quote) {
            handlePrintQuote(quoteId, quote.client?.companyName || 'Quote');
          } else {
            // Fallback if quote not found
            handlePrintQuote(quoteId, 'Quote');
          }
        }}
        onSendQuote={handleSendQuote}
        onDeleteQuote={handleDelete}
        onAcceptQuote={handleAcceptQuote}
        onRejectQuote={handleRejectQuote}
        showClientName={!clientId}
        showPropertyName={!propertyId}
      />

      {/* Audit 07 R-5: deliberate send-target chooser */}
      <ContactSelectDialog
        open={!!sendTarget}
        clientId={sendTarget?.clientId}
        title="Send Quote"
        description="Choose which contact this quote is addressed to. The selected contact's details are frozen onto the issued quote."
        confirmLabel="Send Quote"
        onCancel={() => setSendTarget(null)}
        onConfirm={confirmSendQuote}
      />

      {/* Supplier Selection Modal */}
      <SupplierSelectionModal
        open={supplierSelectionModal.open}
        onClose={() => setSupplierSelectionModal({ open: false, quote: null })}
        quote={supplierSelectionModal.quote}
        onSelectSupplier={(selectedSupplier) => {
          if (supplierSelectionModal.quote) {
            handleSupplierSelected(supplierSelectionModal.quote, selectedSupplier);
          }
        }}
      />

      {/* Pricing Confirmation Modal */}
      <PricingConfirmationModal
        open={pricingConfirmationModal.open}
        onClose={() => setPricingConfirmationModal({ open: false, quote: null, selectedSupplier: null })}
        quote={pricingConfirmationModal.quote}
        selectedSupplier={pricingConfirmationModal.selectedSupplier}
        onConfirm={handlePricingConfirmed}
      />

      {/* Contract Creation Modal */}
      <ContractCreationModal
        open={contractCreationModal.open}
        onClose={() => setContractCreationModal({ open: false, quote: null, selectedSupplier: null })}
        quote={contractCreationModal.quote}
        selectedSupplier={contractCreationModal.selectedSupplier}
        onCreateContract={handleContractCreation}
      />

      {/* Quote Rejection Modal */}
      <QuoteRejectionModal
        open={rejectionModal.open}
        onClose={() => setRejectionModal({ open: false, quote: null })}
        quote={rejectionModal.quote}
        onReject={handleQuoteRejection}
      />
    </Box>
  );
};

export default QuoteListPage;
